import { action, makeObservable, observable, runInAction } from "mobx";
import * as _ from 'lodash';
import request from 'api/request';
import { BaseStore } from "./BaseStore";

export enum AiPromptStatusEnum {
    DRAFT = 1,
    ACTIVE = 2,
    ARCHIVED = 3
}

export enum AiProviderEnum {
    SCALEWAY = 1
}

export interface AiPromptInterface {
    id?: string | null,
    flowKey: string,
    provider: number,
    stepOrder: number,
    promptKey: string,
    name: string,
    description: string | null,
    template: string,
    version: number,
    status: number
}

export class AiPromptStore extends BaseStore {
    @observable prompts: Array<AiPromptInterface> = [];

    constructor() {
        super();

        makeObservable(this);
    }

    @action
    resetStore() {
        this.prompts = [];
    }

    @action
    getPrompts(force: boolean = true) {
        return this.get('ai/prompts', 'prompts', force);
    }

    @action
    async createPrompt(prompt: AiPromptInterface) {
        const res = await request.post('ai/prompts', prompt);
        await this.getPrompts(true);

        const createdPrompt = res.data?.id
            ? _.find(this.prompts, { id: res.data.id })
            : _.findLast(this.prompts, (existingPrompt) =>
                existingPrompt.flowKey === prompt.flowKey &&
                existingPrompt.provider === prompt.provider &&
                existingPrompt.stepOrder === prompt.stepOrder &&
                existingPrompt.promptKey === prompt.promptKey &&
                existingPrompt.name === prompt.name &&
                existingPrompt.version === prompt.version &&
                existingPrompt.status === prompt.status
            );

        return {
            ...prompt,
            id: createdPrompt?.id || res.data?.id || prompt.id || null
        } as AiPromptInterface;
    }

    @action
    async updatePrompt(prompt: AiPromptInterface) {
        await request.put(`ai/prompts/${prompt.id}`, prompt);
        return this.getPrompts(true);
    }

    @action
    async activatePrompt(promptId: string) {
        await request.post(`ai/prompts/${promptId}/activate`, {});
        return this.getPrompts(true);
    }

    @action
    async archivePrompt(promptId: string) {
        await request.delete(`ai/prompts/${promptId}`);
        return this.getPrompts(true);
    }

    @action
    async hardDeletePrompt(promptId: string) {
        try {
            await request.delete(`ai/prompts/${promptId}/hard`);
            runInAction(() => {
                _.remove(this.prompts, (prompt) => prompt.id === promptId);
            });
        }
        catch (error: any) {
            if (error?.response?.status === 404) {
                runInAction(() => {
                    _.remove(this.prompts, (prompt) => prompt.id === promptId);
                });
                return;
            }

            if (error?.response?.status === 400) {
                await this.getPrompts(true);
            }

            throw error;
        }
    }
}

export const aiPromptStore = new AiPromptStore();
